import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../api';
import { tarifFehlerFuer, upgradeAnbieten } from '../billtoro/upgrade';
import { fmtDate } from '../lib/format';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import {
  erledigtUrl,
  mahnlaufUrl,
  mahnungen,
  MAX_LISTE,
  MAX_VERSUCHE,
  type Ausgelassen,
  type Kandidat,
  type Lauf,
  type MahnlaufAntwort,
  type Problem,
  type SendenErgebnis,
} from './MahnlaufDaten';
import { MahnlaufEinstellung } from './MahnlaufEinstellung';
import { PdfDialog } from './PdfDialog';
import { darf, fehlerText, NurAnsehen, tarifFehler } from './tarifHinweis';
import type { Overview } from './types';
import { VersandDialog, type VersandBeleg } from './VersandDialog';
import './mahnlauf.css';

// Seite „Mahnläufe“ (/rechnung/mahnlauf): was heute fällig ist (senden mit einem Klick), E-Mails aus dem Mahnlauf, die
// nicht rausgingen, was ausgelassen wird (mit Grund), die letzten Läufe und die Einstellung. Daten von GET
// …/mahnlauf; nach dem Senden auch die Übersicht neu (Zeichen in der Rechnungsliste).

const mahnUrl = (id: string) => `/api/rechnungstool/mahnung/${id}/pdf`;
type View = { url: string; title: string; beleg?: VersandBeleg };
const ohnePunkt = (s: string) => s.replace(/[.!?]+$/, '');

// Stand oben rechts
function stand(m: MahnlaufAntwort): [string, string] {
  const e = m.einstellung;
  if (!e.an) return ['Aus', 'b-none'];
  if (e.pausiert) return ['Pausiert', 'b-warn'];
  return e.modus === 'senden' ? ['Automatisch senden', 'b-ok'] : ['Nur vorschlagen', 'b-ok'];
}

// Text zum Ergebnis von „Alle senden“
function sendenText(r: SendenErgebnis): string {
  if (r.gesendet + r.fehler === 0) return r.ergebnisse[0]?.text ?? 'Nichts verschickt.';
  if (r.fehler === 0) return `${mahnungen(r.gesendet)} verschickt`;
  return `${r.gesendet} verschickt, ${r.fehler} nicht verschickt`;
}

// weiter: ein Lauf versucht es noch einmal (Mahnläufe an, nicht pausiert, kein Mahnstopp). „offen“ kommt hier nur an,
// wenn der Versand seit 15 Minuten hängt (abgebrochen), dann versucht es der nächste Lauf.
function problemText(p: Problem, weiter: boolean): string {
  if (p.status === 'aufgegeben') return `${MAX_VERSUCHE === 3 ? 'Dreimal' : `${MAX_VERSUCHE}-mal`} nicht verschickt. Bitte von Hand senden.`;
  const wie = p.versuche > 0 ? ` (${p.versuche} von ${MAX_VERSUCHE})` : '';
  const dann = !weiter ? 'Bitte von Hand senden.' : p.status === 'offen' ? 'Nächster Versuch beim nächsten Lauf.' : `Nächster Versuch morgen${wie}.`;
  if (p.status === 'offen') return `Der Versand wurde unterbrochen. ${dann}`;
  return `Die E-Mail ging nicht raus: ${ohnePunkt(p.fehler ?? 'unbekannter Fehler')}. ${dann}`;
}

function laufText(l: Lauf): string {
  const tag = fmtDate(l.tag);
  if (l.status === 'laeuft') return `${tag} · Läuft gerade`;
  if (l.status === 'pausiert') return `${tag} · Pausiert`;
  // nicht bereit (nichts verschickt) oder abgebrochen (vielleicht nach den ersten E-Mails)
  if (l.status === 'fehler') {
    const hinweis = l.hinweis ? `: ${ohnePunkt(l.hinweis)}` : '';
    return l.gesendet ? `${tag} · ${l.gesendet} verschickt, dann abgebrochen${hinweis}` : `${tag} · Nichts verschickt${hinweis}`;
  }
  if (l.modus === 'vorschlagen') return `${tag} · Nur vorschlagen · ${l.faellig} fällig`;
  const teile = [`${tag} · Automatisch · ${l.gesendet} verschickt`];
  if (l.fehler) teile.push(`${l.fehler} nicht verschickt`);
  if (l.hinweis) teile.push(ohnePunkt(l.hinweis));
  return teile.join(' · ');
}

export function Mahnlauf({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  const toast = useToast();
  const firmId = data.firm.id;
  const tarif = data.tarif;
  // Billtoro: senden und als verschickt markieren nur mit dem Recht „Rechnungen“ (der Server prüft)
  const schreiben = darf(tarif, 'rechnungen');
  const [m, setM] = useState<MahnlaufAntwort | null>(null);
  const [error, setError] = useState<string | null>(null);
  // abgewählte Rechnungen in „Heute fällig“ (Standard: alle angehakt)
  const [ab, setAb] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<View | null>(null);
  const [send, setSend] = useState<VersandBeleg | null>(null);
  const aktuell = useRef(firmId);
  aktuell.current = firmId;

  const laden = useCallback(async () => {
    try {
      const r = await api.get<MahnlaufAntwort>(mahnlaufUrl(firmId));
      if (aktuell.current !== firmId) return;
      setM(r);
      setError(null);
    } catch (err) {
      if (aktuell.current === firmId) setError(fehlerText(err) ?? errorText(err));
    }
  }, [firmId]);

  useEffect(() => {
    setM(null);
    setAb(new Set());
    void laden();
  }, [laden]);

  // nach einer Änderung: diese Seite und die Übersicht (Rechnungsliste, Hinweise) neu
  const geaendert = useCallback(async () => {
    await Promise.all([laden(), reload()]);
  }, [laden, reload]);

  if (error && !m) {
    return (
      <div className="notice bad rt-ku">
        <span>{error}</span>
        <button type="button" className="btn small" onClick={() => void laden()}>
          Erneut versuchen
        </button>
      </div>
    );
  }
  if (!m) return <div className="spinner" aria-label="Lädt" />;

  const e = m.einstellung;
  const [standText, standKlasse] = stand(m);
  const gewaehlt = m.faellig.filter((k) => !ab.has(k.rechnungId));
  const tarifPause = e.an && e.pausiert && m.gesperrt;

  async function senden(liste: Kandidat[]) {
    const posten = liste.slice(0, MAX_LISTE).map((k) => ({ rechnungId: k.rechnungId, stufe: k.stufe }));
    if (!posten.length || upgradeAnbieten(tarif, 'mahnlauf')) return;
    setBusy(true);
    try {
      const r = await api.post<SendenErgebnis>(`${mahnlaufUrl(firmId)}/senden`, { posten });
      toast(sendenText(r));
      setAb(new Set());
      await geaendert();
    } catch (err) {
      if (!tarifFehler(err)) toast(errorText(err));
      await laden();
    } finally {
      setBusy(false);
    }
  }

  async function erledigt(mahnungId: string) {
    setBusy(true);
    try {
      await api.post(erledigtUrl(mahnungId));
      toast('Als verschickt markiert');
      await geaendert();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const pdf = (mahnungId: string, titel: string, nummer: string) =>
    setView({ url: mahnUrl(mahnungId), title: `${titel} ${nummer}`, beleg: { art: 'mahnung', id: mahnungId, titel: `${titel} zu ${nummer}` } });

  const einstellung = (
    <MahnlaufEinstellung
      key={`${firmId}:${JSON.stringify(e)}`}
      firmId={firmId}
      einstellung={e}
      tarif={tarif}
      onSaved={async (r) => {
        setM(r);
        await reload();
      }}
    />
  );

  return (
    <div className="ml">
      <div className="rt-head">
        <div>
          <h1>
            Mahnläufe <NurAnsehen tarif={tarif} bereich="rechnungen" />
          </h1>
          <p className="muted">Überfällige Rechnungen von selbst erinnern und mahnen.</p>
        </div>
        <span className={`badge ${standKlasse} ml-stand`}>{standText}</span>
      </div>

      {tarifPause && tarif && (
        <div className="notice warn">
          Ihre Mahnläufe sind pausiert. {tarifFehlerFuer(tarif, 'mahnlauf')?.error ?? ''} Nach dem Wechsel laufen sie von selbst weiter.
        </div>
      )}

      {/* ausgeschaltet: gleich die Einstellung */}
      {!e.an && einstellung}

      {e.an && !e.pausiert && (
        <Faellig
          m={m}
          gewaehlt={gewaehlt}
          busy={busy}
          kann={schreiben}
          onWahl={(id, an) => {
            const neu = new Set(ab);
            if (an) neu.delete(id);
            else neu.add(id);
            setAb(neu);
          }}
          onSenden={() => void senden(gewaehlt)}
        />
      )}

      {m.probleme.length > 0 && (
        <section className="rt-card ml-card" aria-labelledby="ml-probleme">
          <h2 id="ml-probleme">Nicht verschickt</h2>
          <ul className="ml-list">
            {m.probleme.map((p) => (
              <li key={p.mahnungId} className="ml-eintrag">
                <p className="ml-zeile">
                  <b>
                    {p.titel} zu <span className="num">{p.nummer}</span>
                  </b>{' '}
                  · {p.kunde}
                </p>
                <p className={p.status === 'offen' ? 'ml-text muted' : 'ml-text ml-fehler'}>{problemText(p, e.an && !e.pausiert && !p.mahnstopp)}</p>
                <div className="ml-act">
                  {schreiben && (
                    <button type="button" className="linkbtn" onClick={() => setSend({ art: 'mahnung', id: p.mahnungId, titel: `${p.titel} zu ${p.nummer}` })}>
                      Per E-Mail
                    </button>
                  )}
                  <button type="button" className="linkbtn" onClick={() => pdf(p.mahnungId, p.titel, p.nummer)}>
                    PDF
                  </button>
                  {schreiben && (
                    <span>
                      <button type="button" className="linkbtn" disabled={busy} onClick={() => void erledigt(p.mahnungId)}>
                        Als verschickt markieren
                      </button>
                      <span className="muted"> z. B. per Post</span>
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {m.ausgelassen.length > 0 && <AusgelassenListe liste={m.ausgelassen} busy={busy} kann={schreiben} onErledigt={(id) => void erledigt(id)} />}

      {m.laeufe.length > 0 && (
        <section className="rt-card ml-card" aria-labelledby="ml-laeufe">
          <h2 id="ml-laeufe">Letzte Läufe</h2>
          <ul className="ml-laeufe">
            {m.laeufe.map((l) => (
              <li key={l.tag}>{laufText(l)}</li>
            ))}
          </ul>
        </section>
      )}

      {e.an && einstellung}

      <PdfDialog
        url={view?.url ?? null}
        title={view?.title ?? ''}
        download={view ? `${view.url}?download=1` : undefined}
        onClose={() => setView(null)}
        onMail={
          view?.beleg && schreiben
            ? () => {
                setSend(view.beleg ?? null);
                setView(null);
              }
            : undefined
        }
      />
      <VersandDialog beleg={send} tarif={tarif} onClose={() => setSend(null)} onSent={geaendert} />
    </div>
  );
}

// „Heute fällig“ (nur vorschlagen) bzw. „Fällig im nächsten Lauf“ (automatisch senden): angehakt wird gesendet,
// höchstens MAX_LISTE mit einem Klick; ohne kann (Recht „Rechnungen“) nur die Liste
function Faellig({
  m,
  gewaehlt,
  busy,
  kann,
  onWahl,
  onSenden,
}: {
  m: MahnlaufAntwort;
  gewaehlt: Kandidat[];
  busy: boolean;
  kann: boolean;
  onWahl(rechnungId: string, an: boolean): void;
  onSenden(): void;
}) {
  const auto = m.einstellung.modus === 'senden';
  const n = Math.min(gewaehlt.length, MAX_LISTE);
  const alle = gewaehlt.length === m.faellig.length;
  const knopf = busy ? 'Wird gesendet …' : auto ? `Jetzt senden (${n})` : alle ? `Alle senden (${n})` : `Ausgewählte senden (${n})`;
  return (
    <section className="rt-card ml-card" aria-labelledby="ml-faellig">
      <h2 id="ml-faellig">{auto ? 'Fällig im nächsten Lauf' : 'Heute fällig'}</h2>
      {auto && m.faellig.length > 0 && (
        <p className="rt-hint">{m.naechsterLauf === 'heute' ? 'Geht heute ab 9 Uhr von selbst raus.' : 'Geht morgen ab 9 Uhr von selbst raus.'}</p>
      )}
      {m.faellig.length === 0 ? (
        <p className="muted ml-leer">Heute ist nichts fällig.</p>
      ) : (
        <>
          <ul className="ml-list">
            {m.faellig.map((k) => (
              <li key={k.rechnungId} className="ml-kandidat">
                <label className="check ml-check">
                  <input type="checkbox" checked={gewaehlt.includes(k)} disabled={busy || !kann} onChange={(e) => onWahl(k.rechnungId, e.target.checked)} />
                  <span className="ml-zeile">
                    <b className="num">{k.nummer}</b> · {k.kunde}
                  </span>
                </label>
                <span className="ml-titel">{k.titel}</span>
                <span className="ml-offen num">offen {eurC(k.offen)}</span>
                <span className="ml-an muted">an {k.an}</span>
              </li>
            ))}
          </ul>
          {kann && (
            <>
              <div className="ml-senden">
                <button type="button" className="btn primary" disabled={busy || n === 0} onClick={onSenden}>
                  {knopf}
                </button>
                {gewaehlt.length > MAX_LISTE && <span className="muted">Weitere {gewaehlt.length - MAX_LISTE} danach senden.</span>}
              </div>
              <p className="rt-hint ml-tipp">
                Tipp: Erst <Link to="/rechnung/zahlungen">Zahlungen</Link> eintragen, dann senden.
              </p>
            </>
          )}
        </>
      )}
    </section>
  );
}

// Fällig, aber nicht automatisch (zugeklappt): mit Grund, Link in die Liste; ohne E-Mail verschickte letzte Stufe kann
// hier als verschickt markiert werden (kann: Recht „Rechnungen“)
function AusgelassenListe({
  liste,
  busy,
  kann,
  onErledigt,
}: {
  liste: Ausgelassen[];
  busy: boolean;
  kann: boolean;
  onErledigt(mahnungId: string): void;
}) {
  return (
    <details className="rt-card ml-card ml-aus">
      <summary>Ausgelassen ({liste.length})</summary>
      <ul className="ml-list">
        {liste.map((a) => (
          <li key={a.rechnungId} className="ml-eintrag">
            <p className="ml-zeile">
              <b className="num">{a.nummer}</b> · {a.kunde}
              {a.titel ? ` · ${a.titel}` : ''} · offen {eurC(a.offen, a.waehrung)}
            </p>
            <p className="ml-text muted">{a.text}</p>
            <div className="ml-act">
              <Link to="/rechnung/rechnungen?filter=ueberfaellig" className="linkbtn">
                In der Liste
              </Link>
              {kann && a.grund === 'nicht_verschickt' && a.mahnungId && (
                <span>
                  <button type="button" className="linkbtn" disabled={busy} onClick={() => onErledigt(a.mahnungId!)}>
                    Als verschickt markieren
                  </button>
                  <span className="muted"> z. B. per Post</span>
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}
